import * as Rx from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import type { ILeads } from 'v2/interfaces/leads';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';

import initialValues, { getInitialValues } from '../../components/Pages/Leads/Filter/initialValues';
import type { ApiService } from '../api';
import apiService from '../api';
import type { TableService } from '../table';
import tableService from '../table';

class LeadsService {
  private leads$ = new Rx.BehaviorSubject<ILeads[]>([]);
  private filter$ = new Rx.BehaviorSubject<typeof initialValues>(initialValues);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private affiliates$ = new Rx.BehaviorSubject<any[]>([]);
  private campaigns$ = new Rx.BehaviorSubject<any[]>([]);
  private totalItems$ = new Rx.BehaviorSubject<number>(0);

  constructor(
    private apiService: ApiService,
    private tableService: TableService
  ) {}

  public getLeads = (): Rx.Observable<ILeads[]> => this.leads$;

  public getFilter = (): Rx.Observable<any> => this.filter$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public getAffiliates = (): Rx.Observable<any[]> => this.affiliates$;

  public getCampaigns = (): Rx.Observable<any[]> => this.campaigns$;

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public correctFilter = (filter: typeof initialValues) => {
    return {
      created_at_initial: filter.date[0],
      created_at_final: filter.date[1],
      ...filter,
      ...{ date: null }
    };
  };

  public correctFilterToExport = (filter: typeof initialValues) => {
    return {
      created_at_initial: filter.date[0],
      created_at_final: filter.date[1],
      ...filter,
      abandonment: `${filter.abandonment}`,
      ...{ date: null }
    };
  };

  public setFilter(filter: typeof initialValues): void {
    this.filter$.pipe(take(1)).subscribe((currentFilter: any) => {
      this.filter$.next({ ...currentFilter, ...filter });
    });
  }

  public resetPage() {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });

    return this.getPaginationParams();
  }

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public loadAffiliates() {
    this.apiService
      .get('reports/leads/affiliates')
      .pipe(map(response => response.data.map((a: any) => ({ label: a.name, value: a.id }))))
      .subscribe((affiliates: any) => this.affiliates$.next(affiliates));
  }

  public loadCampaigns() {
    this.apiService
      .get('/reports/leads/campaigns')
      .pipe(map(response => response.data.map((a: any) => ({ label: a.key, value: a.id }))))
      .subscribe((campaigns: any) => this.campaigns$.next(campaigns));
  }

  public load() {
    return this.getFilter().pipe(
      map(f => this.correctFilter(f)),
      switchMap(filter => {
        return this.resetPage().pipe(map(params => ({ ...filter, ...params })));
      }),
      tap(() => this.tableService.changeLoading(true)),
      debounceTime(10),
      switchMap(filter => this.apiService.get('reports/leads', filter)),
      tap(leads => this.leads$.next(leads.data)),
      tap(leads => this.paginationResponse$.next(leads.pagination)),
      tap(leads => this.totalItems$.next(leads?.pagination?.total_items)),
      tap(() => this.tableService.changeLoading(false))
    );
  }

  public export() {
    return this.apiService.post('reports/leads/export', this.correctFilterToExport(this.filter$.value));
  }

  public exportBackground(email: string) {
    return this.apiService.post('reports/leads/export-background', {
      ...this.correctFilterToExport(this.filter$.value),
      sender_email: email ? email : null
    });
  }
  public cleanFilter() {
    this.filter$.next(getInitialValues());
  }
}

const leadsService = new LeadsService(apiService, tableService);
export default leadsService;
